// Routing seam: application code imports router APIs from here so the
// React Router implementation can be upgraded in one place. The helpers below
// keep the props and hooks the application used with React Router 5.
import React, { useInsertionEffect, useMemo, useRef } from 'react';
import {
  Navigate,
  useHref,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';

export {
  BrowserRouter,
  Link,
  MemoryRouter,
  matchPath,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

// React Router 5 accepted location objects with state: { pathname, state }.
const navigateTo = (navigate, to, state, replace) => {
  if (to && typeof to === 'object') {
    const { state: locationState, ...path } = to;
    navigate(path, { replace, state: state ?? locationState });
    return;
  }
  navigate(to, { replace, state });
};

// React Router 5 history was a stable object; keep that identity so effects
// depending on it run only when their own inputs change.
export const useHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const latest = useRef({ navigate, location });
  useInsertionEffect(() => {
    latest.current = { navigate, location };
  });
  return useMemo(
    () => ({
      get location() {
        return latest.current.location;
      },
      push: (to, state) =>
        navigateTo(latest.current.navigate, to, state, false),
      replace: (to, state) =>
        navigateTo(latest.current.navigate, to, state, true),
      goBack: () => latest.current.navigate(-1),
    }),
    []
  );
};

// React Router 5 match of the closest route: url/path are the matched part of
// the current location, params include the whole matched route branch.
export const useRouteMatchObject = () => {
  const location = useLocation();
  const params = useParams();
  const splat = params['*'];
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  // Splat routes ("section/*") match a prefix like React Router 5 routes;
  // the matched url is the location without the splat remainder.
  const url = splat
    ? pathname.slice(0, pathname.length - splat.length).replace(/\/$/, '')
    : pathname;
  return useMemo(
    () => ({
      params,
      url,
      path: url,
      isExact: !splat,
    }),
    [params, url, splat]
  );
};

export const useRouteMatch = (pattern) =>
  useMatch({ path: pattern, end: false });

export const withRouter = (Component) => {
  const WithRouter = (props) => {
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatchObject();
    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };
  WithRouter.displayName = `withRouter(${
    Component.displayName || Component.name || 'Component'
  })`;
  return WithRouter;
};

const isModifiedEvent = (event) =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

// LinkContainer of react-router-bootstrap 0.26.3: the child (a Bootstrap nav
// item) gets the href, the active class and client-side navigation. The
// package is CommonJS, and its require of react-router-dom kept the whole
// router (data routers, framework and server code) in the start bundle.
export const LinkContainer = ({
  children,
  onClick,
  replace = false,
  to,
  state,
  activeClassName = 'active',
  className,
  activeStyle,
  style,
  isActive: getIsActive,
  ...props
}) => {
  const path = typeof to === 'object' ? to.pathname || '' : to;
  const navigate = useNavigate();
  const href = useHref(typeof to === 'string' ? { pathname: to } : to);
  const match = useMatch(path);
  const location = useLocation();
  const child = React.Children.only(children);
  let isActive = match;
  if (getIsActive) {
    isActive =
      typeof getIsActive === 'function'
        ? getIsActive(match, location)
        : getIsActive;
  }

  const handleClick = (event) => {
    if (children.props.onClick) children.props.onClick(event);
    if (onClick) onClick(event);
    if (
      !event.defaultPrevented &&
      event.button === 0 &&
      !isModifiedEvent(event)
    ) {
      event.preventDefault();
      navigate(to, { replace, state });
    }
  };

  return React.cloneElement(child, {
    ...props,
    className: [
      className,
      child.props.className,
      isActive ? activeClassName : null,
    ]
      .join(' ')
      .trim(),
    style: isActive ? { ...style, ...activeStyle } : style,
    href,
    onClick: handleClick,
  });
};

// <Redirect> of React Router 5 replaced the history entry by default.
export const Redirect = ({ to, push = false }) => {
  if (to && typeof to === 'object') {
    const { state, ...path } = to;
    return <Navigate to={path} replace={!push} state={state} />;
  }
  return <Navigate to={to} replace={!push} />;
};

// Route elements receive the React Router 5 route props they were written for.
export const RouteComponent = withRouter(
  ({ component: Component, ...props }) => <Component {...props} />
);

// React Router 5 paths used regex params such as ":fieldId([0-9]+)". Newer
// versions match plain params, so the numeric constraint is checked here.
const NUMERIC_PARAM = /:(\w+)\(\[0-9\]\+\)/g;

export const toRoutePath = (path, exact) => {
  const plain = path.replace(NUMERIC_PARAM, ':$1');
  if (exact) return plain;
  const base = plain.replace(/\/$/, '');
  return base ? `${base}/*` : '*';
};

export const getNumericParams = (path) =>
  [...path.matchAll(NUMERIC_PARAM)].map(([, name]) => name);

export const NumericParams = ({ names, children, fallback = null }) => {
  const params = useParams();
  return names.every((name) => /^\d+$/.test(params[name] || ''))
    ? children
    : fallback;
};
